import React, { PropsWithChildren, useCallback } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import classNames from 'classnames';

import Image, { Props as ImageProps } from 'components/Common/Medias/Image';
import Slide from 'components/Common/Slide/Slide';
import { env } from 'settings/env';
import {
  CATEGORY_IMAGE_PRODUCT_HEIGHT,
  CATEGORY_IMAGE_PRODUCT_WIDTH,
} from 'settings/imageSizes';

import styles from './ProductCardImage.module.scss';

export type PropsType = {
  images: ProductImage[];
  classes?: ProductImageClassesType;
  imageProps?: Partial<ImageProps>;
};

export type ProductImage = {
  alt: string;
  src: string;
  linkProps?: LinkProps;
};

type ProductImageClassesType = {
  container?: string;
  image?: string;
  arrow?: string;
};

export default function ProductCardImage({
  children,
  classes,
  images,
  imageProps,
}: PropsWithChildren<PropsType>): JSX.Element {
  const renderImage = useCallback(
    ({ alt, src, linkProps }: ProductImage) => {
      const imgComp = (
        <Image
          alt={alt}
          className={classNames(styles.image, classes?.image)}
          ratio={`${CATEGORY_IMAGE_PRODUCT_WIDTH}/${CATEGORY_IMAGE_PRODUCT_HEIGHT}`}
          src={src ? env.ENV_TWICPICS_URL_MEDIA + src : ''}
          {...imageProps}
        />
      );

      return linkProps ? <Link {...linkProps}>{imgComp}</Link> : imgComp;
    },
    [classes]
  );

  return (
    <div className={classNames(styles.container, classes?.container)}>
      {images.length > 1 ? (
        <Slide
          items={images}
          renderItem={renderImage}
          loopKey={image => image.src}
          options={{
            autoWidth: true,
            perPage: 1,
            type: 'loop',
          }}
          className={styles.slide}
          classes={{
            arrow: classNames(styles.slideArrow, classes?.arrow),
            nextArrow: styles.slideNextArrow,
            prevArrow: styles.slidePrevArrow,
            slide: styles.slideItem,
            track: styles.slideTrack,
          }}
        />
      ) : (
        renderImage(images[0] ?? { src: '', alt: '' })
      )}
      {children}
    </div>
  );
}
